import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trophy, ArrowUpDown } from 'lucide-react';
import type { MedalTallyEntry } from '@/hooks/useApi';

interface Props {
  data: MedalTallyEntry[];
  compact?: boolean;
}

type SortKey = 'gold' | 'silver' | 'bronze' | 'total';

export default function MedalTally({ data, compact = false }: Props) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<SortKey>('gold');

  const sorted = [...data].sort((a, b) => {
    if (a[sortBy] !== b[sortBy]) return b[sortBy] - a[sortBy];
    if (sortBy !== 'total' && a.total !== b.total) return b.total - a.total;
    return b.gold - a.gold;
  });

  const displayed = compact ? sorted.slice(0, 5) : sorted;

  const columns: { key: SortKey; label: string; color: string }[] = [
    { key: 'gold', label: t('scores.gold'), color: 'text-yellow-500' },
    { key: 'silver', label: t('scores.silver'), color: 'text-gray-400' },
    { key: 'bronze', label: t('scores.bronze'), color: 'text-amber-700' },
    { key: 'total', label: t('scores.total'), color: 'text-text-primary' },
  ];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b-2 border-primary/20">
            <th className="py-3 px-2 text-sm font-medium text-text-secondary w-10">#</th>
            <th className="py-3 px-2 text-sm font-medium text-text-secondary">{t('scores.township')}</th>
            {columns.map((col) => (
              <th key={col.key} className="py-3 px-2 text-center">
                <button
                  onClick={() => setSortBy(col.key)}
                  className={`inline-flex items-center gap-1 text-sm font-medium transition-colors
                    ${sortBy === col.key ? col.color + ' font-bold' : 'text-text-secondary hover:text-text-primary'}`}
                >
                  {col.label}
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayed.map((entry, idx) => (
            <tr
              key={entry.township}
              className="border-b border-border/50 hover:bg-[var(--card-hover)] transition-colors"
            >
              <td className="py-3 px-2 text-sm font-medium text-text-secondary">
                {idx < 3 ? (
                  <Trophy className={`w-5 h-5 ${idx === 0 ? 'text-yellow-500' : idx === 1 ? 'text-gray-400' : 'text-amber-700'}`} />
                ) : (
                  idx + 1
                )}
              </td>
              <td className="py-3 px-2 font-medium">{entry.township}</td>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`py-3 px-2 text-center tabular-nums font-semibold ${col.color}`}
                >
                  {entry[col.key]}
                </td>
              ))}
            </tr>
          ))}
          {displayed.length === 0 && (
            <tr>
              <td colSpan={6} className="py-8 text-center text-text-secondary">
                {t('scores.noResults')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
